import React, { useState } from "react";
import { FEEDBACK_PATH, MODEL_COMPLEX_PATH } from "../../functions/src/admin/paths";
import firebase, { auth, schoolDatabase, mainDatabase, currentSchoolKey } from "../api/firebase";
import { useDatabaseVal } from "../api/firebaseHooks";
import OkDlg from "../api/OkDlg";
import { captureException } from "../api/sentry";
import { ModelComplexity } from "../types";
import { useFeedbackStrings } from "./feedback-strings";

const RATING_NUMS = [1, 2, 3, 4, 5];

export default function Feedback({ onFinish }: { onFinish: () => void }) {

    const feedbackStrings = useFeedbackStrings();
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const orderNumConst = modelConstants?.allowSnacks && modelConstants?.allowMeals ? 0 : 1
    const [snacks, setSnacks] = useState(null);
    const [meals, setMeals] = useState(null);
    const [delivery, setDelivery] = useState(null);
    const [platform, setPlatform] = useState(null);
    const [overall, setOverall] = useState(null);
    const [remarks, setRemarks] = useState(null);

    const dlgFeedbackErrorMsg = feedbackStrings.missingRatingsMessage(5 - orderNumConst);

    const isValidFeedback = (modelConstants?.allowSnacks ? snacks : true) && (modelConstants?.allowMeals ? meals : true) && delivery && platform && overall;

    const [dlgFeedbackError, setDlgFeedbackError] = useState(false);
    const [dlgFeedbackCompleted, setDlgFeedbackCompleted] = useState(false);
    const [dlgSubmitFailed, setDlgSubmitFailed] = useState(false);

    const onSendFeedback = () => {

        if (!isValidFeedback) {
            setDlgFeedbackError(true);
            return;
        }
        const feedback = {
            uid: auth.currentUser.uid,
            timestamp: firebase.database.ServerValue.TIMESTAMP,
            snacks,
            meals,
            delivery,
            platform,
            overall,
            remarks
        }
        
        // Store in school database (original behavior)
        schoolDatabase.ref(FEEDBACK_PATH).push(feedback).then((feedbackRef) => {
            // Store minimal trigger data in main database for email notifications
            const triggerData = {
                schoolKey: currentSchoolKey,
                feedbackId: feedbackRef.key
            };

            mainDatabase.ref(FEEDBACK_PATH).push(triggerData).then(() => {
                setDlgFeedbackCompleted(true);
            }).catch((error) => {
                captureException(error instanceof Error ? error : new Error(String(error)), {
                    tags: { component: 'Feedback', operation: 'feedbackMainTriggerPush' },
                    extra: { schoolKey: currentSchoolKey, feedbackId: feedbackRef.key },
                });
                // Feedback is already stored on the school database; user can still see success
                setDlgFeedbackCompleted(true);
            });
        }).catch((error) => {
            captureException(error instanceof Error ? error : new Error(String(error)), {
                tags: { component: 'Feedback', operation: 'feedbackSchoolPush' },
                extra: { schoolKey: currentSchoolKey, uid: auth.currentUser?.uid },
            });
            setDlgSubmitFailed(true);
        });
    }

    return <div className='base_margin_setup' style={{ maxWidth: '800px' }}>
        {dlgFeedbackError &&
            <OkDlg title={feedbackStrings.dlgTitle} msg={dlgFeedbackErrorMsg} onClose={() => setDlgFeedbackError(false)} />
        }
        {dlgSubmitFailed &&
            <OkDlg title={feedbackStrings.dlgTitle} msg={feedbackStrings.submitFailedMsg} onClose={() => setDlgSubmitFailed(false)} />
        }
        {dlgFeedbackCompleted &&
            <OkDlg title={feedbackStrings.dlgTitle} msg={feedbackStrings.dlgThankYou} onClose={() => onFinish()} />
        }
        <div className='order-heading'>{feedbackStrings.pageHeading}</div>
        <div className='order-remark' style={{ paddingBottom: '24px' }}>{feedbackStrings.pageIntro}</div>
        {modelConstants?.allowSnacks && <RateBlockNumbered selected={snacks} onSelect={setSnacks} rateOrderNum={1} rateTitle={feedbackStrings.ratingSnacksTitle} rateSubtitle={feedbackStrings.ratingSnacksSubtitle} awfulLabel={feedbackStrings.awfulLabel} greatLabel={feedbackStrings.greatLabel} />}
        {modelConstants?.allowMeals && <RateBlockNumbered selected={meals} onSelect={setMeals} rateOrderNum={2 - orderNumConst} rateTitle={feedbackStrings.ratingMealsTitle} rateSubtitle={feedbackStrings.ratingMealsSubtitle} awfulLabel={feedbackStrings.awfulLabel} greatLabel={feedbackStrings.greatLabel} />}
        {<RateBlockNumbered selected={delivery} onSelect={setDelivery} rateOrderNum={3 - orderNumConst} rateTitle={feedbackStrings.ratingDeliveryTitle} rateSubtitle={feedbackStrings.ratingDeliverySubtitle} awfulLabel={feedbackStrings.awfulLabel} greatLabel={feedbackStrings.greatLabel} />}
        {<RateBlockNumbered selected={platform} onSelect={setPlatform} rateOrderNum={4 - orderNumConst} rateTitle={feedbackStrings.ratingPlatformTitle} rateSubtitle={feedbackStrings.ratingPlatformSubtitle} awfulLabel={feedbackStrings.awfulLabel} greatLabel={feedbackStrings.greatLabel} />}
        {<RateBlockNumbered selected={overall} onSelect={setOverall} rateOrderNum={5 - orderNumConst} rateTitle={feedbackStrings.ratingOverallTitle} rateSubtitle={feedbackStrings.ratingOverallSubtitle} awfulLabel={feedbackStrings.awfulLabel} greatLabel={feedbackStrings.greatLabel} />}
        {<RateBlockMlText remarks={remarks} onChange={setRemarks} rateOrderNum={6 - orderNumConst} rateTitle={feedbackStrings.ratingMoreTitle} rateSubtitle={feedbackStrings.ratingMoreSubtitle} commentsPlaceholder={feedbackStrings.commentsPlaceholder} />}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button style={{ margin: '0', fontWeight: 'bold', padding: '14px 15px 13px 12px', width: '145px' }} className='credit-rectangle-filled' onClick={onSendFeedback}>{feedbackStrings.send}</button></div>

    </div>
}

function RateHeading({ rateOrderNum, rateTitle, rateSubtitle }: RateHeadingProps) {

    return <div>
        <div className='order-heading' style={{ textAlign: 'start' }}>{rateOrderNum}. {rateTitle}</div>
        <div className='order-remark' style={{ textAlign: 'start', margin: '12px 20px 0' }}>{rateSubtitle}</div>
    </div>
}

type RateHeadingProps = {
    rateTitle: string;
    rateSubtitle: string;
    rateOrderNum: number;
}

function NumberedButtons({ selected, onSelect }: NumberedButtonsProps) {

    return <div className='feedback-numbers'>
        {RATING_NUMS.map(val =>
            <button key={val} className={`${selected === val ? 'selected' : ''}`} style={{ marginLeft: val === 1 ? '20px' : '0' }} onClick={() => onSelect(val)}>{val}</button>
        )
        }
    </div>
}

type NumberedButtonsProps = {
    selected: number | null;
    onSelect: (value: number) => void
}

function RateBlockNumbered({ rateOrderNum, rateTitle, rateSubtitle, selected, onSelect, awfulLabel, greatLabel }: RateBlockNumberedProps) {

    return <div style={{ marginTop: '40px' }}>
        {<RateHeading rateOrderNum={rateOrderNum} rateTitle={rateTitle} rateSubtitle={rateSubtitle} />}
        {<NumberedButtons selected={selected} onSelect={onSelect} />}
        <div className='order-catalog-heading' style={{ width: '410px', padding: '0 28px 0 20px' }}>
            <div className='feedback-text-rate'>{awfulLabel}</div>
            <div className='divider-line feedback-line' />
            <div className='feedback-text-rate'>{greatLabel}</div>
        </div>
    </div>
}

type RateBlockNumberedProps = {
    rateTitle: string;
    rateSubtitle: string;
    rateOrderNum: number;
    selected: number | null;
    onSelect: (value: number) => void;
    awfulLabel: string;
    greatLabel: string;
}

function RateBlockMlText({ rateOrderNum, rateTitle, rateSubtitle, remarks, onChange, commentsPlaceholder }: RateBlockMlTextProps) {

    return <div className='order-core'>
        {<RateHeading rateOrderNum={rateOrderNum} rateTitle={rateTitle} rateSubtitle={rateSubtitle} />}
        <div className='order-remarks-box' style={{ marginTop: '24px' }}>
            <textarea placeholder={commentsPlaceholder} value={remarks ?? ""} onChange={e => onChange(e.target.value)} />
        </div>
    </div>
}

type RateBlockMlTextProps = {
    rateTitle: string;
    rateSubtitle: string;
    rateOrderNum: number;
    remarks: string | null;
    onChange: (value: string) => void;
    commentsPlaceholder: string;
}
